"use client";

import { useState } from "react";
import { Check, Share2 } from "lucide-react";

import { cx } from "@tripcart/traveler-ui/cx";

import { useTranslator } from "@/lib/translation-context";

import styles from "./share-button.module.css";

/**
 * `.ttl-share` — the guide's share control at the end of the chip row.
 *
 * Prefers the platform share sheet and falls back to copying the URL, because
 * `navigator.share` exists on phones and almost nowhere on desktop. Both paths
 * confirm: a share control that does nothing visible reads as broken even when
 * it worked. The confirmation replaces the label rather than firing a toast —
 * the feedback belongs where the click was.
 *
 * A user dismissing the share sheet throws `AbortError`, which is not a
 * failure and must not fall through to copying: doing that would quietly put
 * the URL on their clipboard after they said no.
 */
export function ShareButton({
  title,
  variant = "chip",
  className,
}: {
  title: string;
  /** `"chip"` — the labelled pill at the end of the title row, which is where
   *  the guide draws it. `"icon"` — a 36px round button for the sticky tab
   *  strip, where a phone gets it instead: the chip row scrolls away with the
   *  title, and Share is the one control on this page a traveller reaches for
   *  after reading rather than before. */
  variant?: "chip" | "icon";
  className?: string;
}) {
  const t = useTranslator();
  const [copied, setCopied] = useState(false);

  async function share() {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        // Anything else (no permission, unsupported payload) → fall through
        // to the clipboard rather than leaving the tap dead.
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context, denied permission). Nothing
      // useful to say, and a thrown error here would take the page down.
    }
  }

  const icon = copied ? (
    <Check aria-hidden className={styles.icon} strokeWidth={2} />
  ) : (
    <Share2 aria-hidden className={styles.icon} strokeWidth={2} />
  );

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={share}
        aria-label={t("Share this tour")}
        // 36px — `--height-control-default`. That is the whole hit area: a
        // control's target is its own box, and the tab row's padding belongs
        // to the tabs, not to this button. So it meets WCAG 2.5.8 Target Size
        // (Minimum, 24px) at AA and does NOT meet 2.5.5 (Enhanced, 44px).
        // This comment claimed the opposite until 2026-09-17 ("clears the
        // 44px touch target once the tab row's own padding is counted"),
        // which is the worst kind of wrong: it named a guarantee nobody could
        // then check. If 44px is wanted here, a 44px `.iconButton` is the
        // change.
        className={cx(styles.iconButton, className)}
        data-copied={copied ? "" : undefined}
      >
        {icon}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={share}
      aria-label={t("Share this tour")}
      className={cx(styles.chip, className)}
    >
      {icon}
      {copied ? t("Link copied") : t("Share")}
    </button>
  );
}
